/**
 * AccountExplainer — explainer for the ACCOUNT Structural Duality Principle (St).
 *
 * ACCOUNT is the level-1 SDP that pairs with RES.BALANCE along the
 * linear ↔ compact-closed adjunction. It certifies that consume/release
 * accounting composes as one resource lifecycle — a RES.BALANCE witness
 * can be stacked through arbitrary refactorings without producing a
 * forbidden pattern. With RES.BALANCE (primal) it closes as the ICG-St
 * Safety Envelope.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CalloutBox,
  ExplainerCard,
  SdpLevelBadge,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.color;
const LEVEL = COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.level;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-account",
  kind: `SSM · Compositionality Principle (S2 · level ${LEVEL})`,
  title: "ACCOUNT",
  subtitle: "St · linear ↔ compact-closed · Neutralization Theorems: R1 + R2",
  accent: ACCENT,
  accentAlt: DUAL_PAIR_COLOR["RES.BALANCE"].color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "RES.BALANCE (primal DPG)",
    href: "#explainer-res-balance",
    accent: DUAL_PAIR_COLOR["RES.BALANCE"].color,
  },
  {
    label: "ICG-St (Safety Envelope)",
    href: "#explainer-icg-st",
    accent: DUAL_PAIR_COLOR["RES.BALANCE"].color,
  },
  {
    label: "RES.CONSUME (R1)",
    href: "#explainer-res-consume",
    accent: THEOREM_COLOR["RES.CONSUME"],
  },
  {
    label: "RES.RELEASE (R2)",
    href: "#explainer-res-release",
    accent: THEOREM_COLOR["RES.RELEASE"],
  },
  { label: "Compact-closed", href: "#explainer-compact-closed" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ACCOUNT</strong> is the safety story for stacking the resource lifecycle: it certifies
      that "consume within budget and release on scope exit" composes safely — by the linear ↔
      compact-closed duality, not by inspection.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you can write "this resource is both spent within budget and discharged" (a RES.BALANCE
        witness), you immediately want to stack two of them across a refactor. ACCOUNT is the
        certificate that says stacking is safe — the joint account is itself a RES.BALANCE witness,
        not a weaker one.
      </p>
      <p>
        Where RES.BALANCE delivers the guarantee inside one regime, ACCOUNT delivers
        compositionality across regimes. Together they close as the ICG-St Safety Envelope.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ACCOUNT is the Structural Duality Principle that pairs the linear consumption functor and
        the compact-closed release functor at the SDP level:{" "}
        <MathInline>{tex`(- \otimes A) \dashv (A^{*} \otimes -)`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        <strong>1-categorical seam vs SDP graded adjunction</strong> (per{" "}
        <code>theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md</code>). At the 1-categorical level
        the RES.BALANCE seam declares an <em>absolute empty hom-set</em>: the
        consume-only-or-release-only morphism either exists or it does not, in the strict binary
        sense. The ACCOUNT adjunction operates at the <strong>2-categorical, graded</strong> level:
        its counit (the compact evaluation) supplies, <em>at each grade</em>, the precise discharge
        a bridge must produce to cross the accounting seam — structurally inexpressible without the
        witness, expressible with it.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        ACCOUNT is what lets the ICG-St Safety Envelope reduce the audit surface from "a RES.BALANCE
        witness per regime" to "one envelope" carrying the adjunction unit and counit.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If two resource accounts stack but the joint is
        not a RES.BALANCE witness, ACCOUNT rules it out structurally — there is no graded-adjunction
        counit certifying the stack. The forbidden composition is{" "}
        <em>structurally inexpressible</em> at the SDP level.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-St" accent={ACCENT}>
        RES.BALANCE (DPG) <MathInline>{tex`\otimes`}</MathInline> ACCOUNT (SDP) = ICG-St, the single
        two-sided certificate. Audit surface collapses from <em>(DPG witness) + (SDP witness)</em>{" "}
        to <em>one envelope</em> carrying the{" "}
        <MathInline>{tex`\mathrm{linear} \dashv \mathrm{compact\text{-}closed}`}</MathInline> unit
        and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — SDPs and ICGs (§7, §7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "Seam adjunction / bridge Kan theory",
      path: "theory/structural/SEAM-ADJUNCTION-BRIDGE-KAN.md",
      note: "Authoritative treatment of the empty-hom-set seam vs the graded SDP adjunction; AGENTS.md routes here.",
    },
    {
      label: "RES.CONSUME kernel module",
      path: "agda/Kernel/RES/CONSUME/Neutralization.agda",
    },
    {
      label: "RES.RELEASE kernel module",
      path: "agda/Kernel/RES/RELEASE/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <>
    <div style={{ marginBottom: "0.5rem" }}>
      <SdpLevelBadge level={LEVEL} accent={ACCENT} />
    </div>
    <AdjunctionDiagram
      leftCat="Linear"
      rightCat="Compact-closed"
      leftAdjoint="(−⊗A)"
      rightAdjoint="(A*⊗−)"
      accent={ACCENT}
      ariaLabel="ACCOUNT duality: linear consumption left-adjoint to compact-closed release with the graded crossing unit and counit"
    />
  </>
);

export function AccountExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
